import type { Aircraft, Airport } from '@/lib/reference'
import type { RawLeg } from './types'

/**
 * Reference rows a file needs that the tables do not have yet.
 *
 * A daily report is written by the operation, not by the reference tables, so
 * it is always slightly ahead of them: a tail joins the fleet, a station opens.
 * The first drop had two of each — `VN-A527` and `VN-A650` flying, and `SAI`
 * (Siem Reap Angkor) served 38 times, none of them in a table.
 *
 * Refusing the file is wrong: the flights are real. Importing silently is
 * worse: a station with no zone cannot be placed on the clock, and a tail with
 * no trio is judged by no matrix at all. So the rows are **created with the
 * import**, and the import screen says so and lets them be corrected on the
 * spot — or left alone and corrected later in Settings, where they appear as
 * added rows like any other manual entry.
 *
 * Every default here is a guess made from data already in the tables, never
 * from the air, and every one of them is editable before the import runs.
 */

export interface AircraftDraft {
  regn: string
  group: string
  category: string
  type: string
  seatConfig: string
  rank: number
  /** What the file called it (321, 320, 330), for the line the user reads. */
  acType: string
  /** Legs in the incoming files flown by this tail. */
  legs: number
}

export interface AirportDraft {
  iata: string
  /** OurAirports calls this column `ident`; for a new station it is the code. */
  icao: string
  name: string
  municipality: string
  countryCode: string
  timezone: string
  /** Legs in the incoming files touching this station. */
  legs: number
}

export interface MissingRows {
  aircraft: AircraftDraft[]
  airports: AirportDraft[]
}

export function missingRows(
  raws: RawLeg[],
  fleet: Aircraft[],
  airportsByIata: Map<string, Airport>,
): MissingRows {
  const known = new Set(fleet.map((tail) => tail.regn))
  const tails = new Map<string, { acType: string; legs: number }>()
  const stations = new Map<string, number>()

  for (const leg of raws) {
    if (leg.regn && !known.has(leg.regn)) {
      const seen = tails.get(leg.regn)
      if (seen) seen.legs += 1
      else tails.set(leg.regn, { acType: leg.acType, legs: 1 })
    }
    for (const station of [leg.dep, leg.arr]) {
      if (!station || airportsByIata.has(station)) continue
      stations.set(station, (stations.get(station) ?? 0) + 1)
    }
  }

  return {
    aircraft: [...tails.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([regn, seen]) => ({ regn, ...draftFromFleet(seen.acType, fleet), acType: seen.acType, legs: seen.legs })),
    airports: [...stations.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([iata, legs]) => ({
        iata,
        icao: iata,
        name: '',
        municipality: '',
        countryCode: '',
        timezone: '',
        legs,
      })),
  }
}

/**
 * The trio a new tail most likely belongs to, read off the existing fleet.
 *
 * The file states only an aircraft type code — 321 — which is not a trio and
 * cannot become one on its own (05-DU-LIEU-THAM-CHIEU 5.1: "A320" is both a
 * group of 14 CEO tails and the category of 8 wet-leased A32W). So the guess
 * is the **most common trio among the tails that already carry that code**,
 * together with its most common seat configuration and rank: a new A321 starts
 * out like the other A321s until somebody says otherwise.
 */
function draftFromFleet(
  acType: string,
  fleet: Aircraft[],
): { group: string; category: string; type: string; seatConfig: string; rank: number } {
  const code = acType.replace(/\D/g, '')
  const family = code ? `A${code}` : ''
  const candidates = fleet.filter(
    (tail) => tail.group === family || tail.category === family || tail.category.includes(code),
  )

  if (candidates.length === 0) {
    return { group: family, category: family, type: '', seatConfig: '', rank: 0 }
  }

  const trios = new Map<string, Aircraft[]>()
  for (const tail of candidates) {
    const key = `${tail.group}/${tail.category}/${tail.type}`
    const list = trios.get(key)
    if (list) list.push(tail)
    else trios.set(key, [tail])
  }
  const [, biggest] = [...trios.entries()].sort((a, b) => b[1].length - a[1].length)[0]
  const model = biggest[0]

  return {
    group: model.group,
    category: model.category,
    type: model.type,
    seatConfig: model.seatConfig,
    rank: model.rank,
  }
}

/** Seats parsed the same way the build script and the aircraft screen do it. */
export function parseCabins(text: string): Record<string, number> {
  const cabins: Record<string, number> = {}
  for (const match of text.toUpperCase().matchAll(/(\d+)([A-Z])|([A-Z])(\d+)/g)) {
    const [, digitsFirst, cabinAfter, cabinFirst, digitsAfter] = match
    if (cabinAfter) cabins[cabinAfter] = Number(digitsFirst)
    else if (cabinFirst) cabins[cabinFirst] = Number(digitsAfter)
  }
  return cabins
}

/** A draft as the aircraft table stores it. */
export function aircraftFields(draft: AircraftDraft) {
  const cabins = parseCabins(draft.seatConfig)
  return {
    regn: draft.regn,
    group: draft.group.trim().toUpperCase(),
    category: draft.category.trim().toUpperCase(),
    type: draft.type.trim(),
    seatConfig: draft.seatConfig.trim().toUpperCase(),
    cabins,
    seats: Object.values(cabins).reduce((sum, count) => sum + count, 0),
    maxCyclesPerDay: 0,
    maxBlockMinutes: 0,
    limitCountry: [],
    limitAirport: [],
    // Rank 9 is the lowest, so a tail nobody has ranked never wins a swap it
    // has not been judged for.
    rank: draft.rank || 9,
    // Said out loud on the row itself: reference data that nobody has checked
    // is the most dangerous kind (BUSINESS-RULES 11).
    note: 'Created from a schedule import. Limits not set.',
  }
}

/** A draft as the airport table stores it. Vietnam is DOM, everywhere else ITL. */
export function airportFields(draft: AirportDraft) {
  const code = draft.countryCode.trim().toUpperCase()
  return {
    iata: draft.iata,
    icao: draft.icao.trim() || draft.iata,
    name: draft.name.trim() || draft.iata,
    continent: '',
    countryCode: code,
    municipality: draft.municipality.trim(),
    timezone: draft.timezone.trim(),
    type: '',
    scope: code === 'VN' ? 'DOM' : 'ITL',
  }
}
